/**
 * ReferenceImageDialog Component
 *
 * Dialog for selecting a reference image for image-to-image generation.
 * Provides three methods:
 * 1. Drag and drop an image
 * 2. Upload from file system (file picker)
 * 3. Browse existing images from the Fias file system
 */

import { useState, useCallback, useRef, DragEvent } from 'react';
import { Upload, FolderOpen, ImagePlus, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileBrowser } from '@/components/files/FileBrowser';
import { api, UserFileResponse } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type { ReferenceImage } from '../types/image-generator.types';

export interface ReferenceImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReferenceSelected: (ref: ReferenceImage) => void;
}

const ACCEPTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

const MAX_REFERENCE_SIZE = 20 * 1024 * 1024; // 20 MB

type View = 'main' | 'browse';

export function ReferenceImageDialog({
  open,
  onOpenChange,
  onReferenceSelected,
}: ReferenceImageDialogProps) {
  const [view, setView] = useState<View>('main');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UserFileResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setView('main');
    setIsDragging(false);
    setIsUploading(false);
    setSelectedFile(null);
  }, []);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!isUploading) {
        if (!newOpen) reset();
        onOpenChange(newOpen);
      }
    },
    [isUploading, onOpenChange, reset],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
        toast({
          title: 'Unsupported file type',
          description: 'Please select a PNG, JPEG, WebP, or GIF image.',
          variant: 'destructive',
        });
        return;
      }

      if (file.size > MAX_REFERENCE_SIZE) {
        toast({
          title: 'File too large',
          description: 'Reference images must be under 20 MB.',
          variant: 'destructive',
        });
        return;
      }

      setIsUploading(true);
      try {
        // Ensure Reference Images folder exists
        const { folder: refFolder } = await api.ensureReferenceImagesFolder();

        // Delete any existing file with the same name to avoid duplicates
        const existing = await api.listFiles({
          folderId: refFolder.folderId,
          status: 'active',
          search: file.name,
        });
        const duplicate = existing.files.find((f) => f.fileName === file.name);
        if (duplicate) {
          await api.permanentDeleteFile(duplicate.fileId);
        }

        // Upload new file
        const upload = await api.getUploadUrl({
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          category: 'generated',
        });

        await fetch(upload.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        await api.confirmUpload(upload.fileId, {
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          s3Key: upload.s3Key,
          category: 'generated',
          folderId: refFolder.folderId,
        });

        const thumbnailUrl = URL.createObjectURL(file);
        onReferenceSelected({
          imageRef: upload.s3Key,
          thumbnailUrl,
          source: 'uploaded',
        });

        onOpenChange(false);
        reset();
      } catch (err) {
        console.error('Failed to upload reference image:', err);
        toast({
          title: 'Upload failed',
          description: err instanceof Error ? err.message : 'An error occurred',
          variant: 'destructive',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [onReferenceSelected, onOpenChange, reset],
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) uploadFile(file);
    },
    [uploadFile],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) uploadFile(file);
      e.target.value = '';
    },
    [uploadFile],
  );

  // File system browser handlers
  const handleFileSelect = useCallback((file: UserFileResponse) => {
    if (!file.mimeType?.startsWith('image/')) {
      toast({
        title: 'Not an image',
        description: 'Please select an image file.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleConfirmFileSelection = useCallback(async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      // Get presigned download URL for thumbnail
      const { downloadUrl } = await api.getDownloadUrl(selectedFile.fileId);

      onReferenceSelected({
        fileId: selectedFile.fileId,
        thumbnailUrl: downloadUrl,
        source: 'file-system',
      });

      onOpenChange(false);
      reset();
    } catch (err) {
      console.error('Failed to select reference image:', err);
      toast({
        title: 'Selection failed',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, onReferenceSelected, onOpenChange, reset]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={view === 'browse' ? 'max-w-4xl h-[80vh] flex flex-col' : 'max-w-md'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImagePlus className="h-5 w-5" />
            {view === 'browse' ? 'Select from Files' : 'Reference Image'}
          </DialogTitle>
          <DialogDescription>
            {view === 'browse'
              ? 'Select an image from your files to use as a reference.'
              : 'Add a reference image for image-to-image generation.'}
          </DialogDescription>
        </DialogHeader>

        {view === 'main' && (
          <div className="space-y-4">
            {/* Drag & drop zone */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50 hover:bg-muted/30'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Drop an image here</p>
                  <p className="text-xs text-muted-foreground">
                    or click to browse. PNG, JPEG, WebP, GIF up to 20 MB.
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handleFileInputChange}
              />
            </div>

            {/* Browse from file system */}
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => setView('browse')}
              disabled={isUploading}
            >
              <FolderOpen className="h-4 w-4" />
              Browse from My Files
            </Button>
          </div>
        )}

        {view === 'browse' && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setView('main');
                  setSelectedFile(null);
                }}
              >
                Back
              </Button>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
              <FileBrowser
                onFileSelect={handleFileSelect}
                multiSelect={false}
                status="active"
                className="h-full"
              />
            </div>

            {selectedFile && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <ImagePlus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate font-medium text-sm">{selectedFile.fileName}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setSelectedFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button size="sm" onClick={handleConfirmFileSelection} disabled={isUploading}>
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Use as Reference'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
